import api from '@/lib/api';
import type {
  ReportRunRequest,
  ReportRunResponse,
  SavedReportDefinition,
  RetailActivityType,
} from '@pms/shared';

// ─── Query / payload types ────────────────────────────────────────────────────

export interface ListSavedReportsQuery {
  propertyId: string;
}

export interface CreateSavedReportPayload {
  propertyId: string;
  name: string;
  /** The validated builder config (re-validated server-side on load/re-run). */
  config: ReportRunRequest;
}

export interface UpdateSavedReportPayload {
  name?: string | undefined;
  config?: ReportRunRequest | undefined;
}

export interface RunReportPayload extends ReportRunRequest {
  propertyId: string;
}

export interface ActivityLogQuery {
  propertyId: string;
  eventType?: RetailActivityType | undefined;
  from?: string | undefined;
  to?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

/** One row of the derived activity-log UNION feed. */
export interface ActivityLogRow {
  id: string;
  type: RetailActivityType;
  /** ISO timestamp. */
  occurredAt: string;
  actor?: string | null;
  summary: string;
  /** DECIMAL(12,2) as string, when the event carries an amount. */
  amount?: string | null;
}

export interface ActivityLogMeta {
  page: number;
  limit: number;
  total: number;
}

export interface ActivityLogResponse {
  data: ActivityLogRow[];
  meta: ActivityLogMeta;
}

// ─── API functions ────────────────────────────────────────────────────────────

/** Run a report — a fresh ephemeral execution (mutation, never cached). */
export async function runReport(payload: RunReportPayload): Promise<ReportRunResponse> {
  const { data } = await api.post<ReportRunResponse>('/retail/reports/run', payload);
  return data;
}

export async function fetchSavedReports(
  query: ListSavedReportsQuery,
): Promise<SavedReportDefinition[]> {
  const { data } = await api.get<SavedReportDefinition[]>(
    '/retail/reports/definitions',
    { params: query },
  );
  return data;
}

export async function createSavedReport(
  payload: CreateSavedReportPayload,
): Promise<SavedReportDefinition> {
  const { data } = await api.post<SavedReportDefinition>(
    '/retail/reports/definitions',
    payload,
  );
  return data;
}

export async function updateSavedReport(
  id: string,
  payload: UpdateSavedReportPayload,
): Promise<SavedReportDefinition> {
  const { data } = await api.patch<SavedReportDefinition>(
    `/retail/reports/definitions/${id}`,
    payload,
  );
  return data;
}

export async function deleteSavedReport(id: string): Promise<void> {
  await api.delete(`/retail/reports/definitions/${id}`);
}

export async function fetchActivityLog(
  query: ActivityLogQuery,
): Promise<ActivityLogResponse> {
  const { data } = await api.get<ActivityLogResponse>('/retail/reports/activity', {
    params: query,
  });
  return data;
}
